import { api } from '@packages/backend/convex/_generated/api';
import type { MenuCvx } from '@packages/shared';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { ChevronDown, Columns, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { MenuItemsRearrangeList } from './menu-items/components/MenuItemsRearrangeList';
import { MenuItemsTable } from './menu-items/components/MenuItemsTable';

export type ColumnVisibleType = 'extra' | 'standard';

export const Route = createFileRoute('/r/$restaurantId/menu/menu-items')({
  component: Component,
});

function Component() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const { data, isError, isPending } = useQuery(api.menus.getByRestaurantId, { restaurantId });
  const [columnsVisible, setColumnsVisible] = useState<ColumnVisibleType>('standard');
  const [categoryUuidFilter, setCategoryUuidFilter] = useState<string>('');
  const [isRearranging, setIsRearranging] = useState(false);
  const [isCreatingMenu, setIsCreatingMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const clearSearch = () => setSearchTerm('');

  // Reset `isRearranging` and clear search when switching categories
  useEffect(() => {
    setIsRearranging(false);
    clearSearch();
  }, [categoryUuidFilter]);

  // Focus on the search input when "All" category tab is selected
  useLayoutEffect(() => {
    if (categoryUuidFilter === '') {
      searchInputRef.current?.focus();
    }
  }, [categoryUuidFilter]);

  if (isPending) return <LoadingIcon />;
  if (isError) return null;
  const menu: MenuCvx | null = data;

  // If no menu is setup yet
  if (menu == null) {
    const handleSetupMenu = async () => {
      try {
        setIsCreatingMenu(true);
        await convex.mutation(api.menus.initMenu, { restaurantId });
        toast.success('New menu created successfully');
      } catch (error) {
        const description = getUserErrorMessage(error);
        toast.error('Unable to create new menu', { description });
      } finally {
        setIsCreatingMenu(false);
      }
    };
    return (
      <ActionButton loading={isCreatingMenu} onClick={handleSetupMenu}>
        Setup menu
      </ActionButton>
    );
  }

  const { categories, menuItems, modifierGroups, modifiers } = menu;

  if (categories.length === 0) {
    return (
      <div className={'bg-white p-4 text-center border'}>
        <div>Add some categories first.</div>
      </div>
    );
  }

  // Filter menu items based on search term and category
  const filteredMenuItems = menuItems.filter((item) => {
    const matchesCategory = categoryUuidFilter ? item.categoryUuid === categoryUuidFilter : true;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div>
      <div className="mb-2">
        {/* Category (filter) tabs */}
        <Tabs className="w-full" defaultValue={''} onValueChange={setCategoryUuidFilter}>
          <TabsList className="inline-flex flex-wrap bg-white dark:bg-muted h-auto">
            <TabsTrigger
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              value=""
            >
              All
            </TabsTrigger>
            {categories.map((c) => (
              <TabsTrigger
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground hover:bg-primary/10"
                key={c.uuid}
                value={c.uuid}
              >
                {c.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {!isRearranging && (
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="relative max-w-xs">
            <Input
              className="pr-8"
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search menu items..."
              ref={searchInputRef}
              value={searchTerm}
            />
            {searchTerm && (
              <ActionButton
                className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 p-0"
                onClick={clearSearch}
                size="icon"
                variant="ghost"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </ActionButton>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ActionButton className="flex" size="sm" variant="outline">
                <Columns className="h-4 w-4" />
                Columns
                <ChevronDown className="h-4 w-4" />
              </ActionButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={'end'} className="w-56">
              <DropdownMenuRadioGroup
                onValueChange={(value) =>
                  setColumnsVisible(value === 'extra' ? 'extra' : 'standard')
                }
                value={columnsVisible}
              >
                <DropdownMenuRadioItem value="standard">Prices, mods</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="extra">Visibility, desc, GST</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* `key` is set to `categoryIdFilter` to force a re-render when categoryIdFilter value changes */}
      <div key={categoryUuidFilter}>
        {isRearranging ? (
          <MenuItemsRearrangeList
            menuId={menu._id}
            menuItems={menuItems.filter((mi) => mi.categoryUuid === categoryUuidFilter)}
            onRearrangingDone={() => {
              setIsRearranging(false);
              clearSearch();
            }}
          />
        ) : (
          <MenuItemsTable
            categories={categories}
            // Replace '' with undefined
            categoryUuidFilter={categoryUuidFilter === '' ? undefined : categoryUuidFilter}
            columnsVisible={columnsVisible}
            menuId={menu._id}
            menuItems={filteredMenuItems}
            modifierGroups={modifierGroups}
            modifiers={modifiers}
            onStartSorting={() => {
              setIsRearranging(true);
              clearSearch();
            }}
          />
        )}
      </div>
    </div>
  );
}
